import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FormDataState,
  CareRecipientData,
  LegalRepresentativeData,
  PreventedCaregiverData,
  SubstituteCaregiverData,
  CommercialProviderData,
  PrivateIndividualData,
  CareSessionEntry,
} from '../../types';

// Initial states for each section
const initialCareRecipient: CareRecipientData = {
  lastName: '',
  name: '',
  birthday: '',
  address: '',
  houseNumber: '',
  zipCode: '',
  city: '',
  phoneNumber: '',
  insurance: '',
  insuranceNumber: '',
  levelOfCare: '',
  certificate: '',
  legalRepresentation: '',
};

const initialLegalRepresentative: LegalRepresentativeData = {
  lastName: '',
  name: '',
  address: '',
  houseNumber: '',
  zipCode: '',
  city: '',
  phoneNumber: '',
  document: '',
};

const initialPreventedCaregiver: PreventedCaregiverData = {
  lastName: '',
  name: '',
  birthday: '',
  address: '',
  houseNumber: '',
  zipCode: '',
  city: '',
  phoneNumber: '',
  isProfessionalCarer: '',
  carePeriod: '',
  absenceStartDate: '',
  absenceEndDate: '',
  absenceReason: '',
};

const initialSubstituteCaregiver: SubstituteCaregiverData = {
  lastName: '',
  name: '',
  address: '',
  houseNumber: '',
  zipCode: '',
  city: '',
  phoneNumber: '',
  isPaidByOthers: '',
  isCommerciallyActive: '',
  relationshipType: '',
  marriageRelationship: '',
  sameHousehold: '',
  howFound: '',
};

const createEmptyCareSessionEntry = (): CareSessionEntry => ({
  availableDates: {
    date: '',
    duration: '',
    amount: '',
  },
  durationOfAbsence: '',
});

const initialCommercialProvider: CommercialProviderData = {
  activeDates: [createEmptyCareSessionEntry()],
  accountHolderName: '',
  iban: '',
  bank: '',
  dataProcessingConsent: '',
  termsAgreement: '',
  flexxiConsent: '',
  paymentConfirmation: '',
};

const initialPrivateIndividual: PrivateIndividualData = {
  individualDates: [createEmptyCareSessionEntry()],
  hasTravelCosts: '',
  distanceKm: '',
  originLocation: '',
  destinationLocation: '',
  travelCost: '',
  receiptUpload: [],
  hasEarningsLoss: '',
  earningsLossAmount: '',
  accountHolderName: '',
  iban: '',
  bank: '',
  dataProcessingConsent: '',
  termsAgreement: '',
  flexxiConsent: '',
  paymentConfirmation: '',
};

const initialState: FormDataState = {
  careRecipient: initialCareRecipient,
  legalRepresentative: initialLegalRepresentative,
  preventedCaregiver: initialPreventedCaregiver,
  substituteCaregiver: initialSubstituteCaregiver,
  commercialProvider: initialCommercialProvider,
  privateIndividual: initialPrivateIndividual,
};

const formDataSlice = createSlice({
  name: 'formData',
  initialState,
  reducers: {
    // Care Recipient (Section 1)
    setCareRecipientField: (
      state,
      action: PayloadAction<{ field: keyof CareRecipientData; value: string }>
    ) => {
      state.careRecipient[action.payload.field] = action.payload.value;
    },
    setCareRecipient: (state, action: PayloadAction<Partial<CareRecipientData>>) => {
      state.careRecipient = { ...state.careRecipient, ...action.payload };
    },
    resetCareRecipient: (state) => {
      state.careRecipient = initialCareRecipient;
    },

    // Legal Representative (Section 2)
    setLegalRepresentativeField: (
      state,
      action: PayloadAction<{ field: keyof LegalRepresentativeData; value: string }>
    ) => {
      state.legalRepresentative[action.payload.field] = action.payload.value;
    },
    setLegalRepresentative: (state, action: PayloadAction<Partial<LegalRepresentativeData>>) => {
      state.legalRepresentative = { ...state.legalRepresentative, ...action.payload };
    },
    resetLegalRepresentative: (state) => {
      state.legalRepresentative = initialLegalRepresentative;
    },

    // Prevented Caregiver (Section 3)
    setPreventedCaregiverField: (
      state,
      action: PayloadAction<{ field: keyof PreventedCaregiverData; value: string }>
    ) => {
      state.preventedCaregiver[action.payload.field] = action.payload.value;
    },
    setPreventedCaregiver: (state, action: PayloadAction<Partial<PreventedCaregiverData>>) => {
      state.preventedCaregiver = { ...state.preventedCaregiver, ...action.payload };
    },
    resetPreventedCaregiver: (state) => {
      state.preventedCaregiver = initialPreventedCaregiver;
    },

    // Substitute Caregiver (Section 4)
    setSubstituteCaregiverField: (
      state,
      action: PayloadAction<{ field: keyof SubstituteCaregiverData; value: string }>
    ) => {
      state.substituteCaregiver[action.payload.field] = action.payload.value;
    },
    setSubstituteCaregiver: (state, action: PayloadAction<Partial<SubstituteCaregiverData>>) => {
      state.substituteCaregiver = { ...state.substituteCaregiver, ...action.payload };
    },
    resetSubstituteCaregiver: (state) => {
      state.substituteCaregiver = initialSubstituteCaregiver;
    },

    // Commercial Provider (Section 5)
    setCommercialProviderField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<CommercialProviderData, 'activeDates'>;
        value: string;
      }>
    ) => {
      (state.commercialProvider[action.payload.field] as string) = action.payload.value;
    },
    setCommercialProvider: (state, action: PayloadAction<Partial<CommercialProviderData>>) => {
      state.commercialProvider = { ...state.commercialProvider, ...action.payload };
    },
    addCommercialProviderDate: (state) => {
      state.commercialProvider.activeDates.push(createEmptyCareSessionEntry());
    },
    updateCommercialProviderDate: (
      state,
      action: PayloadAction<{ index: number; entry: CareSessionEntry }>
    ) => {
      state.commercialProvider.activeDates[action.payload.index] = action.payload.entry;
    },
    setCommercialProviderDurationOfAbsence: (
      state,
      action: PayloadAction<{ index: number; value: string }>
    ) => {
      const { index, value } = action.payload;
      if (state.commercialProvider.activeDates[index]) {
        state.commercialProvider.activeDates[index].durationOfAbsence = value;
      }
    },
    removeCommercialProviderDate: (state, action: PayloadAction<number>) => {
      state.commercialProvider.activeDates.splice(action.payload, 1);
      if (state.commercialProvider.activeDates.length === 0) {
        state.commercialProvider.activeDates.push(createEmptyCareSessionEntry());
      }
    },
    resetCommercialProvider: (state) => {
      state.commercialProvider = initialCommercialProvider;
    },

    // Private Individual (Section 6)
    setPrivateIndividualField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<PrivateIndividualData, 'individualDates' | 'receiptUpload'>;
        value: string;
      }>
    ) => {
      (state.privateIndividual[action.payload.field] as string) = action.payload.value;
    },
    setPrivateIndividual: (state, action: PayloadAction<Partial<PrivateIndividualData>>) => {
      state.privateIndividual = { ...state.privateIndividual, ...action.payload };
    },
    addPrivateIndividualDate: (state) => {
      state.privateIndividual.individualDates.push(createEmptyCareSessionEntry());
    },
    updatePrivateIndividualDate: (
      state,
      action: PayloadAction<{ index: number; entry: CareSessionEntry }>
    ) => {
      state.privateIndividual.individualDates[action.payload.index] = action.payload.entry;
    },
    setPrivateIndividualDurationOfAbsence: (
      state,
      action: PayloadAction<{ index: number; value: string }>
    ) => {
      const { index, value } = action.payload;
      if (state.privateIndividual.individualDates[index]) {
        state.privateIndividual.individualDates[index].durationOfAbsence = value;
      }
    },
    removePrivateIndividualDate: (state, action: PayloadAction<number>) => {
      state.privateIndividual.individualDates.splice(action.payload, 1);
      if (state.privateIndividual.individualDates.length === 0) {
        state.privateIndividual.individualDates.push(createEmptyCareSessionEntry());
      }
    },
    addReceiptUpload: (state, action: PayloadAction<string>) => {
      state.privateIndividual.receiptUpload.push(action.payload);
    },
    removeReceiptUpload: (state, action: PayloadAction<number>) => {
      state.privateIndividual.receiptUpload.splice(action.payload, 1);
    },
    resetPrivateIndividual: (state) => {
      state.privateIndividual = initialPrivateIndividual;
    },

    // Bulk load form data (e.g., from API)
    loadFormData: (state, action: PayloadAction<Partial<FormDataState>>) => {
      if (action.payload.careRecipient) {
        state.careRecipient = { ...initialCareRecipient, ...action.payload.careRecipient };
      }
      if (action.payload.legalRepresentative) {
        state.legalRepresentative = {
          ...initialLegalRepresentative,
          ...action.payload.legalRepresentative,
        };
      }
      if (action.payload.preventedCaregiver) {
        state.preventedCaregiver = {
          ...initialPreventedCaregiver,
          ...action.payload.preventedCaregiver,
        };
      }
      if (action.payload.substituteCaregiver) {
        state.substituteCaregiver = {
          ...initialSubstituteCaregiver,
          ...action.payload.substituteCaregiver,
        };
      }
      if (action.payload.commercialProvider) {
        state.commercialProvider = {
          ...initialCommercialProvider,
          ...action.payload.commercialProvider,
        };
      }
      if (action.payload.privateIndividual) {
        state.privateIndividual = {
          ...initialPrivateIndividual,
          ...action.payload.privateIndividual,
        };
      }
    },

    // Reset all form data
    resetFormData: () => initialState,
  },
});

export const {
  setCareRecipientField,
  setCareRecipient,
  resetCareRecipient,
  setLegalRepresentativeField,
  setLegalRepresentative,
  resetLegalRepresentative,
  setPreventedCaregiverField,
  setPreventedCaregiver,
  resetPreventedCaregiver,
  setSubstituteCaregiverField,
  setSubstituteCaregiver,
  resetSubstituteCaregiver,
  setCommercialProviderField,
  setCommercialProvider,
  addCommercialProviderDate,
  updateCommercialProviderDate,
  setCommercialProviderDurationOfAbsence,
  removeCommercialProviderDate,
  resetCommercialProvider,
  setPrivateIndividualField,
  setPrivateIndividual,
  addPrivateIndividualDate,
  updatePrivateIndividualDate,
  setPrivateIndividualDurationOfAbsence,
  removePrivateIndividualDate,
  addReceiptUpload,
  removeReceiptUpload,
  resetPrivateIndividual,
  loadFormData,
  resetFormData,
} = formDataSlice.actions;

export default formDataSlice.reducer;

